import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Handshake, Copy, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ConnectReveal = ({ postId, isNeed, testPrefix = "post-card" }) => {
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(false);

  const accentText = isNeed ? "text-[#00F0FF]" : "text-[#B57BEE]";

  const toggleContact = async () => {
    if (contact) {
      setContact(null);
      return;
    }
    setLoading(true);
    try {
      const r = await axios.get(`${API}/posts/${postId}/contact`);
      setContact(r.data.contact);
    } catch {
      toast.error("Could not load contact details. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const copyContact = async () => {
    try {
      await navigator.clipboard.writeText(contact);
      toast.success("Contact details copied.");
    } catch {
      toast.error("Copy failed — long-press to copy manually.");
    }
  };

  return (
    <div>
      <AnimatePresence mode="wait" initial={false}>
        {contact ? (
          <motion.div
            key="contact"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className={`flex items-center justify-between gap-3 border px-4 py-3.5 ${
              isNeed ? "border-[#00F0FF]/40 bg-[#00F0FF]/5" : "border-[#8A2BE2]/50 bg-[#8A2BE2]/10"
            }`}
            data-testid={`${testPrefix}-contact-${postId}`}
          >
            <span className={`font-mono-accent text-xs sm:text-sm break-all leading-relaxed ${accentText}`}>{contact}</span>
            <div className="flex items-center gap-1 shrink-0">
              <button
                data-testid={`${testPrefix}-copy-${postId}`}
                onClick={copyContact}
                aria-label="Copy contact details"
                className="w-9 h-9 grid place-items-center text-slate-400 hover:text-slate-100 active:text-[#00F0FF] transition-colors"
              >
                <Copy size={14} />
              </button>
              <button
                data-testid={`${testPrefix}-hide-${postId}`}
                onClick={() => setContact(null)}
                className="font-mono-accent text-[10px] uppercase tracking-[0.15em] text-slate-500 hover:text-slate-300 transition-colors px-2 py-2"
              >
                Hide
              </button>
            </div>
          </motion.div>
        ) : (
          <motion.button
            key="connect"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            data-testid={`${testPrefix}-connect-${postId}`}
            onClick={toggleContact}
            disabled={loading}
            className={`w-full inline-flex items-center justify-center gap-2.5 px-4 py-4 sm:py-3.5 font-mono-accent text-[11px] sm:text-xs font-bold uppercase tracking-[0.2em] border transition-all duration-300 disabled:opacity-50 active:scale-[0.98] ${
              isNeed
                ? "border-[#00F0FF]/40 text-[#00F0FF] hover:bg-[#00F0FF]/10 hover:shadow-[0_0_18px_rgba(0,240,255,0.15)]"
                : "border-[#8A2BE2]/50 text-[#B57BEE] hover:bg-[#8A2BE2]/15 hover:shadow-[0_0_18px_rgba(138,43,226,0.2)]"
            }`}
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Handshake size={14} />}
            {loading ? "Connecting…" : "Connect"}
          </motion.button>
        )}
      </AnimatePresence>
      <p className="mt-3 text-center font-mono-accent text-[9px] tracking-[0.18em] text-slate-700 uppercase whitespace-nowrap">
        Private until you connect
      </p>
    </div>
  );
};
